import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { assets, siteConfig } from "@/data/site";
import { fetchPartitionStallsGoogleReviews, type LiveGooglePlaceReviews } from "@/lib/googlePlaceReviews";

const LIVE_REVIEW_MAX_ATTEMPTS = 2;
const LIVE_REVIEW_RETRY_DELAY_MS = 750;

function ReviewStars({ rating, label }: { rating: number; label: string }) {
  return (
    <span className="contact-booking-reviews__stars" role="img" aria-label={label}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star key={index} aria-hidden="true" className={rating >= index + 0.5 ? "is-filled" : undefined} />
      ))}
    </span>
  );
}

export function ContactGoogleReviews() {
  const [place, setPlace] = useState<LiveGooglePlaceReviews | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let retryTimer: number | null = null;

    const loadReviews = async (attempt: number) => {
      try {
        const result = await fetchPartitionStallsGoogleReviews();
        if (!active) return;
        setPlace(result);
        setFailed(false);
      } catch {
        if (!active) return;
        if (attempt < LIVE_REVIEW_MAX_ATTEMPTS) {
          retryTimer = window.setTimeout(() => { void loadReviews(attempt + 1); }, LIVE_REVIEW_RETRY_DELAY_MS);
          return;
        }
        setFailed(true);
      }
    };

    void loadReviews(1);
    return () => {
      active = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, []);

  const ratingLabel = useMemo(() => place
    ? `${place.rating.toFixed(1)} out of 5 based on ${new Intl.NumberFormat("en-US").format(place.userRatingCount)} Google reviews`
    : "Google review summary loading",
  [place]);

  return (
    <section className="contact-booking-reviews" aria-labelledby="contact-booking-reviews-heading" data-google-review-feed="authorized-live-places">
      <div className="contact-booking-reviews__banner">
        <img src={assets.googleReviewsBanner} width="1280" height="410" alt="Google Reviews" loading="lazy" />
      </div>
      <div className="contact-booking-reviews__content">
        <div className="contact-booking-reviews__profile" aria-live="polite">
          <span>Partition Stalls</span>
          <strong id="contact-booking-reviews-heading">Google Reviews</strong>
          <p>{siteConfig.address.display}</p>
          {place ? (
            <div className="contact-booking-reviews__summary" aria-label={ratingLabel}>
              <strong>{place.rating.toFixed(1)}</strong>
              <ReviewStars rating={place.rating} label={ratingLabel} />
              <span>{new Intl.NumberFormat("en-US").format(place.userRatingCount)} Google reviews</span>
            </div>
          ) : (
            <p className="contact-booking-reviews__status">{failed ? "Official review profile available" : "Loading current Google rating"}</p>
          )}
        </div>
        <div className="contact-booking-reviews__actions">
          <a href={siteConfig.googleReviews.read} target="_blank" rel="noopener noreferrer" aria-label="Read current Partition Stalls reviews on Google (opens in a new tab)">
            <span>Read Google Reviews</span><ArrowUpRight aria-hidden="true" />
          </a>
          <a href={siteConfig.googleReviews.leave} target="_blank" rel="noopener noreferrer" aria-label="Leave a Google review for Partition Stalls (opens in a new tab)">
            <span>Write a Review</span><ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>
      <p className="contact-booking-reviews__source">{place ? "Rating summary supplied by Google’s live Partition Stalls profile." : "Ratings and customer comments remain on Google’s live profile."}</p>
    </section>
  );
}
